import { useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';

import { ApiError } from '../../shared/api/client.ts';
import {
  useBaixarInstalador,
  useDefinirBloqueioDoAgente,
  useExcluirAgente,
  useInstalador,
  usePedirAtualizacao,
  usePedirVarredura,
  type ResultadoInstalador,
} from '../../shared/api/queries.ts';
import {
  ROTULO_DO_SISTEMA,
  descreverOrigem,
  estaOnline,
  versaoCurta,
  type Agente,
} from '../../shared/api/tipos.ts';
import { formatarDataHora, formatarDataSimples } from '../../shared/format.ts';

/**
 * As instalações do agente de um cliente, dentro da linha dele.
 *
 * Elas moram aqui, e não numa tela própria, porque a pergunta que se faz sobre
 * uma instalação é sempre sobre um cliente: "o PDV da loja está mandando as
 * notas?". Separadas, as duas telas obrigavam a casar nome de máquina com
 * razão social na cabeça.
 *
 * Nada aqui cria instalação: quem baixa o instalador é a coluna da listagem
 * (`BotaoInstalarClient`), e o vínculo manual acontece no computador do
 * cliente, com o técnico na frente dele.
 */
export function InstalacoesDoCliente({
  agentes,
  podeAdministrar,
}: {
  agentes: readonly Agente[];
  podeAdministrar: boolean;
}) {
  const [bloqueando, setBloqueando] = useState<Agente | null>(null);
  const [excluindo, setExcluindo] = useState<Agente | null>(null);
  const [varrendo, setVarrendo] = useState<Agente | null>(null);
  const [atualizando, setAtualizando] = useState<Agente | null>(null);

  const bloquear = useDefinirBloqueioDoAgente();
  const excluir = useExcluirAgente();
  // A mesma consulta do botão de instalar, com a mesma chave de cache: é dela
  // que sai a versão publicada, a única coisa com que comparar a da máquina.
  const publicada = useInstalador().data?.versaoPublicada ?? null;

  return (
    <Box sx={{ my: 1 }}>
      {agentes.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
          Nenhuma máquina vinculada ainda. Use <strong>Instalar client</strong> na linha
          do cliente e rode o arquivo baixado no computador dele — a instalação aparece
          aqui no primeiro contato.
        </Alert>
      ) : (
      <Table size="small">
        <TableBody>
          {agentes.map((agente) => (
            <TableRow key={agente.id} sx={{ opacity: agente.bloqueadoEm === null ? 1 : 0.7 }}>
              <TableCell sx={{ borderBottom: 'none' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{agente.nome}</span>
                  {agente.situacao === 'bloqueado' && (
                    <Tooltip title={agente.motivoDoBloqueio ?? ''}>
                      <Chip size="small" color="error" label="Bloqueado" />
                    </Tooltip>
                  )}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {agente.sistema !== null && `${ROTULO_DO_SISTEMA[agente.sistema]} · `}
                  {descreverOrigem(agente)} · {agente.intervaloSegundos}s
                </Typography>
                <EstadoDaVarredura agente={agente} />
                <EstadoDaAtualizacao agente={agente} />
              </TableCell>

              <TableCell sx={{ whiteSpace: 'nowrap', borderBottom: 'none' }}>
                {/* Só o prefixo: é o que identifica a credencial sem revelar
                    nada que sirva para autenticar. */}
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  ctb_{agente.prefixoToken}…
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vinculado {formatarDataHora(agente.tokenEmitidoEm)}
                </Typography>
                <Versao agente={agente} publicada={publicada} />
              </TableCell>

              <TableCell sx={{ whiteSpace: 'nowrap', borderBottom: 'none' }}>
                <Presenca agente={agente} />
              </TableCell>

              <TableCell align="right" sx={{ borderBottom: 'none' }}>
                {/* Sem tooltip nos botões de ação: o rótulo já está visível, e
                    o popper fica sobre o botão vizinho, atrapalhando o clique
                    seguinte. */}
                {podeAdministrar && (
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    {agente.situacao === 'ativo' && (
                      <Button
                        size="small"
                        startIcon={<HistoryOutlinedIcon />}
                        onClick={() => setVarrendo(agente)}
                      >
                        Reenviar período
                      </Button>
                    )}

                    {/* Só quando há o que trocar: um botão que recusaria com
                        "já está na versão publicada" é um botão que ensina a
                        desconfiar dos outros. Máquina que não informa versão é
                        agente anterior a este recurso: ele ignora o pedido em
                        silêncio, e a linha ficaria "entregue" sem nunca
                        concluir. Ali só o instalador resolve, e é o que o aviso
                        ao lado da versão explica. */}
                    {agente.situacao === 'ativo' &&
                      publicada !== null &&
                      agente.versao !== null &&
                      agente.versao !== publicada && (
                        <Button
                          size="small"
                          startIcon={<SystemUpdateAltOutlinedIcon />}
                          onClick={() => setAtualizando(agente)}
                        >
                          Atualizar
                        </Button>
                      )}

                    {agente.situacao === 'ativo' ? (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<BlockOutlinedIcon />}
                        onClick={() => setBloqueando(agente)}
                      >
                        Bloquear
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="success"
                        startIcon={<LockOpenOutlinedIcon />}
                        onClick={() => bloquear.mutate({ id: agente.id, bloqueado: false })}
                        loading={bloquear.isPending}
                      >
                        Liberar
                      </Button>
                    )}

                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlinedIcon />}
                      onClick={() => setExcluindo(agente)}
                    >
                      Excluir
                    </Button>
                  </Stack>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}

      {bloquear.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {mensagemDoErro(bloquear.error)}
        </Alert>
      )}
      {excluir.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {mensagemDoErro(excluir.error)}
        </Alert>
      )}

      <DialogoExclusao
        agente={excluindo}
        excluindo={excluir.isPending}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => {
          if (excluindo === null) return;
          excluir.mutate(excluindo.id);
          setExcluindo(null);
        }}
      />

      <DialogoBloqueio
        agente={bloqueando}
        aoFechar={() => setBloqueando(null)}
        aoConfirmar={(motivo) => {
          if (bloqueando === null) return;
          bloquear.mutate({ id: bloqueando.id, bloqueado: true, motivo });
          setBloqueando(null);
        }}
      />

      <DialogoVarredura agente={varrendo} aoFechar={() => setVarrendo(null)} />

      <DialogoAtualizacao
        agente={atualizando}
        publicada={publicada}
        aoFechar={() => setAtualizando(null)}
      />
    </Box>
  );
}

/**
 * A coluna "Instalar" da listagem de clientes.
 *
 * Fica na linha fechada de propósito: instalar é a primeira coisa que se faz
 * com um cliente novo, e justamente aí não há nenhuma instalação para a gaveta
 * mostrar — obrigar a expandir uma linha vazia para achar o botão era o
 * caminho mais longo para a tarefa mais comum.
 *
 * O botão só avisa quem clicou. O diálogo é da tela, não da linha: um `Dialog`
 * montado aqui dentro continuaria **dentro da linha** na árvore do React, e
 * portal não muda isso — clicar em qualquer campo do diálogo borbulharia até o
 * `onClick` da linha e abriria a edição do cliente por baixo.
 */
export function BotaoInstalarClient({ aoInstalar }: { aoInstalar: () => void }) {
  const instalador = useInstalador();
  const indisponivel = instalador.data?.disponivel === false;

  const botao = (
    <Button
      size="small"
      variant="outlined"
      startIcon={<DownloadOutlinedIcon />}
      // A linha inteira abre a edição do cliente; este clique para aqui.
      onClick={(evento) => {
        evento.stopPropagation();
        aoInstalar();
      }}
      disabled={indisponivel || instalador.isPending}
    >
      Instalar client
    </Button>
  );

  // Desabilitado não dispara evento de mouse, então o motivo precisa de um
  // elemento por fora para o tooltip ter onde se pendurar.
  if (!indisponivel) return botao;

  return (
    <Tooltip title="O servidor não tem o executável do agente publicado.">
      <span>{botao}</span>
    </Tooltip>
  );
}

/**
 * O diálogo existe para deixar claro o que o download é: **uma instalação a
 * mais** naquele cliente, com credencial própria, uma por máquina.
 *
 * Endereço do servidor não se pergunta mais aqui. Ele é constante do agente
 * (`SERVIDOR_FIXO`), e um campo nesta tela era por onde entrava o erro que só
 * aparecia dias depois, do outro lado: instalador emitido com o endereço de
 * teste, loja instalada, nenhuma nota chegando e nada errado à vista.
 */
export function DialogoInstalador({
  cliente,
  instalacoes,
  aoFechar,
}: {
  /** Nulo com o diálogo fechado: é o cliente que o abre. */
  cliente: { id: string; razaoSocial: string } | null;
  /** Quantas instalações o cliente já tem, para a tela dizer o que vem depois. */
  instalacoes: number;
  aoFechar: () => void;
}) {
  const baixar = useBaixarInstalador();
  const [baixado, setBaixado] = useState<ResultadoInstalador | null>(null);

  async function confirmar() {
    if (cliente === null) return;
    try {
      setBaixado(await baixar.mutateAsync({ empresaId: cliente.id }));
    } catch {
      // A mensagem fica em `baixar.error`; o diálogo continua aberto.
    }
  }

  return (
    <Dialog
      open={cliente !== null}
      onClose={baixar.isPending ? undefined : aoFechar}
      fullWidth
      maxWidth="sm"
      slotProps={{
        transition: {
          onEnter: () => {
            setBaixado(null);
            baixar.reset();
          },
        },
      }}
    >
      <DialogTitle>Instalar client em {cliente?.razaoSocial}</DialogTitle>

      {baixado !== null ? (
        <>
          <DialogContent>
            <Alert severity="success" sx={{ mt: 1 }}>
              <AlertTitle>{baixado.nome}</AlertTitle>
              Leve o arquivo para a máquina do cliente e execute: ele se instala, entra na
              bandeja e já começa a sincronizar. A credencial desta instalação é a{' '}
              <code>ctb_{baixado.prefixoToken}</code>, e ela aparece na linha do cliente
              assim que a máquina der o primeiro sinal.
              {baixado.instalacoesDoCliente > 1 &&
                ` Este cliente agora tem ${baixado.instalacoesDoCliente} instalações, e as outras continuam valendo.`}
            </Alert>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={aoFechar}>
              Fechar
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {baixar.isError && (
                <Alert severity="error">{mensagemDoErro(baixar.error)}</Alert>
              )}

              <DialogContentText variant="body2">
                O arquivo baixado é o agente com a configuração deste cliente dentro.
                Leve-o para o computador da loja e execute: ele se instala, registra o
                arranque com o Windows e já começa a mandar as NFC-e.
              </DialogContentText>

              {instalacoes > 0 && (
                <Alert severity="info">
                  Este cliente já tem {instalacoes} instalação(ões). Esta é mais uma, com
                  credencial própria: as que já estão rodando não param. Use um instalador
                  por máquina — duas cópias do mesmo arquivo em computadores diferentes
                  disputariam a mesma credencial.
                </Alert>
              )}

              <DialogContentText variant="body2" sx={{ color: 'text.secondary' }}>
                O endereço do escritório já vem dentro do agente, igual em todas as
                lojas: não há o que configurar na máquina do cliente.
              </DialogContentText>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={aoFechar} disabled={baixar.isPending}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadOutlinedIcon />}
              loading={baixar.isPending}
              onClick={() => void confirmar()}
            >
              Baixar instalador
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/**
 * O que aconteceu com o último pedido de reenvio.
 *
 * Os três instantes são o que separa "o período não tinha nota" de "a máquina
 * nunca leu o pedido" — sem isso, as duas coisas apareceriam iguais na tela:
 * nenhuma nota nova.
 */
function EstadoDaVarredura({ agente }: { agente: Agente }) {
  const varredura = agente.varredura;
  if (varredura === null) return null;

  const periodo = `${formatarDataSimples(varredura.de)} a ${formatarDataSimples(varredura.ate)}`;

  const texto =
    varredura.concluidaEm !== null
      ? `reenvio de ${periodo}: ${varredura.enviadas} nova(s), ${varredura.repetidas} já arquivada(s)` +
        (varredura.falhas > 0 ? `, ${varredura.falhas} recusada(s)` : '')
      : varredura.entregueEm !== null
        ? `reenviando ${periodo}, desde ${formatarDataHora(varredura.entregueEm)}`
        : `reenvio de ${periodo} pedido; desce no próximo contato da máquina`;

  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {texto}
    </Typography>
  );
}

/**
 * O diálogo do reenvio.
 *
 * O padrão é o mês passado inteiro mais o corrente, que é o período que o
 * escritório ainda vai fechar — e é também o que o agente sobe sozinho quando
 * é instalado. Pedir de novo um período já enviado não duplica nada: arquivar
 * é idempotente pela chave de acesso.
 */
function DialogoVarredura({
  agente,
  aoFechar,
}: {
  agente: Agente | null;
  aoFechar: () => void;
}) {
  const pedir = usePedirVarredura();
  const [periodo, setPeriodo] = useState(periodoPadrao);

  async function confirmar() {
    if (agente === null) return;
    try {
      await pedir.mutateAsync({ id: agente.id, de: periodo.de, ate: periodo.ate });
      aoFechar();
    } catch {
      // A mensagem fica em `pedir.error`; o diálogo continua aberto.
    }
  }

  return (
    <Dialog
      open={agente !== null}
      onClose={pedir.isPending ? undefined : aoFechar}
      fullWidth
      maxWidth="xs"
      slotProps={{
        transition: {
          onEnter: () => {
            setPeriodo(periodoPadrao());
            pedir.reset();
          },
        },
      }}
    >
      <DialogTitle>Reenviar período</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {pedir.isError && <Alert severity="error">{mensagemDoErro(pedir.error)}</Alert>}

          <DialogContentText variant="body2">
            {agente?.nome} vai reler o PDV e mandar de novo as NFC-e emitidas no período.
            O pedido desce no próximo contato da máquina — se ela estiver desligada,
            espera por ela. Notas já arquivadas não duplicam.
          </DialogContentText>

          <Stack direction="row" spacing={2}>
            <TextField
              label="De"
              type="date"
              value={periodo.de}
              onChange={(evento) =>
                setPeriodo((atual) => ({ ...atual, de: evento.target.value }))
              }
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Até"
              type="date"
              value={periodo.ate}
              onChange={(evento) =>
                setPeriodo((atual) => ({ ...atual, ate: evento.target.value }))
              }
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar} disabled={pedir.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={periodo.de === '' || periodo.ate === '' || periodo.de > periodo.ate}
          loading={pedir.isPending}
          onClick={() => void confirmar()}
        >
          Pedir reenvio
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * A versão do executável que a máquina está rodando.
 *
 * O valor é o SHA-256 do arquivo, encurtado: ninguém decora um hash, mas doze
 * dígitos bastam para casar o que está na loja com o que o escritório
 * publicou. O chip aparece só quando há diferença — uma coluna que diz
 * "atualizada" em todas as linhas é uma coluna que ninguém lê, e aí a única
 * linha que importa passa batida.
 */
function Versao({ agente, publicada }: { agente: Agente; publicada: string | null }) {
  const desatualizada = publicada !== null && agente.versao !== publicada;

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Typography variant="caption" color="text.secondary">
        versão {versaoCurta(agente.versao)}
      </Typography>
      {desatualizada && (
        <Tooltip
          title={
            agente.versao === null
              ? 'Esta máquina não informa a versão: é um agente anterior à atualização pelo painel. Para trocá-lo, baixe o instalador e rode na loja.'
              : `Publicada: ${versaoCurta(publicada)}`
          }
        >
          <Chip size="small" color="warning" variant="outlined" label="desatualizada" />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * O que aconteceu com o último pedido de troca de executável.
 *
 * Os mesmos três marcos da varredura, pelo mesmo motivo: "pedi e nada mudou"
 * tem três respostas diferentes — a máquina não leu o pedido, está trocando, ou
 * tentou e não conseguiu. Sem isso, as três apareceriam iguais na tela.
 */
function EstadoDaAtualizacao({ agente }: { agente: Agente }) {
  const atualizacao = agente.atualizacao;
  if (atualizacao === null) return null;

  const alvo = versaoCurta(atualizacao.versaoAlvo);

  if (atualizacao.erro !== null) {
    return (
      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
        atualização para {alvo} não deu certo: {atualizacao.erro} — a loja segue na versão
        de antes
      </Typography>
    );
  }

  const texto =
    atualizacao.concluidaEm !== null
      ? `atualizada para ${alvo} em ${formatarDataHora(atualizacao.concluidaEm)}`
      : atualizacao.entregueEm !== null
        ? `trocando para ${alvo}, desde ${formatarDataHora(atualizacao.entregueEm)}`
        : `atualização para ${alvo} pedida; desce no próximo contato da máquina`;

  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {texto}
    </Typography>
  );
}

/**
 * O diálogo da troca de executável.
 *
 * Diz o que vai acontecer na loja, porque ninguém de lá vai ser avisado: o
 * agente baixa, confere o arquivo, se substitui e volta — o ícone da bandeja
 * pisca e pronto. E diz o que é mais fácil esquecer: se a máquina estiver
 * desligada, o pedido espera por ela, como o reenvio de período.
 */
function DialogoAtualizacao({
  agente,
  publicada,
  aoFechar,
}: {
  agente: Agente | null;
  publicada: string | null;
  aoFechar: () => void;
}) {
  const atualizar = usePedirAtualizacao();

  async function confirmar() {
    if (agente === null) return;
    try {
      await atualizar.mutateAsync(agente.id);
      aoFechar();
    } catch {
      // A mensagem fica em `atualizar.error`; o diálogo continua aberto.
    }
  }

  return (
    <Dialog
      open={agente !== null}
      onClose={atualizar.isPending ? undefined : aoFechar}
      fullWidth
      maxWidth="xs"
      slotProps={{ transition: { onEnter: () => atualizar.reset() } }}
    >
      <DialogTitle>Atualizar {agente?.nome}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {atualizar.isError && (
            <Alert severity="error">{mensagemDoErro(atualizar.error)}</Alert>
          )}

          <DialogContentText variant="body2">
            A máquina vai baixar o agente publicado ({versaoCurta(publicada)}), conferir o
            arquivo e se trocar sozinha — sem janela nenhuma na loja: o ícone da bandeja
            desaparece e volta em alguns segundos. A configuração dela, inclusive a senha
            do banco do PDV, fica como está.
          </DialogContentText>

          <DialogContentText variant="body2">
            O pedido desce no próximo contato da máquina. Se ela estiver desligada, espera
            por ela. Se a troca não der certo, o agente continua trabalhando na versão de
            antes e o motivo aparece nesta linha.
          </DialogContentText>

          <Alert severity="info">
            Atualize um caixa primeiro e confira se ele volta a dar sinal. A versão
            publicada é a mesma para todas as lojas: aplicá-la em todas de uma vez também
            espalha de uma vez um executável com problema.
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar} disabled={atualizar.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<SystemUpdateAltOutlinedIcon />}
          loading={atualizar.isPending}
          onClick={() => void confirmar()}
        >
          Pedir atualização
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Do dia 1º do mês passado até hoje. */
function periodoPadrao(): { de: string; ate: string } {
  const hoje = new Date();
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return { de: emIso(inicio), ate: emIso(hoje) };
}

function emIso(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/**
 * Resumo para a linha fechada do cliente: quantas máquinas, quantas de pé.
 * É o que precisa ser legível sem expandir nada.
 */
export function ResumoDasInstalacoes({ agentes }: { agentes: readonly Agente[] }) {
  if (agentes.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        sem instalação
      </Typography>
    );
  }

  const online = agentes.filter((agente) => estaOnline(agente)).length;

  return (
    <Chip
      size="small"
      color={online > 0 ? 'success' : 'error'}
      label={
        agentes.length === 1
          ? online === 1
            ? 'Online'
            : 'Offline'
          : `${online}/${agentes.length} online`
      }
    />
  );
}

/**
 * Online é derivado, não um campo que o servidor guarda: é o último sinal de
 * presença comparado com o intervalo configurado na máquina. Por isso vem
 * sempre acompanhado da hora do último contato — é o dado bruto, e é o que
 * permite ao escritório julgar sozinho um caso de fronteira.
 */
function Presenca({ agente }: { agente: Agente }) {
  if (agente.situacao === 'bloqueado') {
    return (
      <Typography variant="body2" color="text.secondary">
        bloqueado
      </Typography>
    );
  }

  const online = estaOnline(agente);

  return (
    <Stack spacing={0.25}>
      {/* Verde de pé, vermelho fora do ar: é um semáforo, e a cor precisa
          responder antes do texto. */}
      <Chip
        size="small"
        color={online ? 'success' : 'error'}
        label={online ? 'Online' : 'Offline'}
        sx={{ width: 'fit-content' }}
      />
      <Typography variant="caption" color="text.secondary">
        {agente.ultimoContatoEm === null
          ? 'nunca deu sinal'
          : formatarDataHora(agente.ultimoContatoEm)}
      </Typography>
    </Stack>
  );
}

/**
 * Excluir tira a instalação da lista e derruba o token junto. O que foi
 * arquivado fica — é período encerrado, sob obrigação de guarda —, e é
 * exatamente isso que o diálogo precisa dizer antes de alguém confirmar.
 */
function DialogoExclusao({
  agente,
  excluindo,
  aoFechar,
  aoConfirmar,
}: {
  agente: Agente | null;
  excluindo: boolean;
  aoFechar: () => void;
  aoConfirmar: () => void;
}) {
  return (
    <Dialog open={agente !== null} onClose={aoFechar} fullWidth maxWidth="xs">
      <DialogTitle>Excluir {agente?.nome}</DialogTitle>

      <DialogContent>
        <DialogContentText variant="body2">
          O token desta instalação para de valer e ela sai da lista. As notas que ela já
          enviou continuam arquivadas. Para voltar a sincronizar, é preciso refazer o
          vínculo no computador do cliente.
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar}>Cancelar</Button>
        <Button variant="contained" color="error" loading={excluindo} onClick={aoConfirmar}>
          Excluir
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * O motivo do bloqueio é obrigatório de propósito: meses depois, é a única
 * explicação de por que aquela máquina parou de enviar.
 */
function DialogoBloqueio({
  agente,
  aoFechar,
  aoConfirmar,
}: {
  agente: Agente | null;
  aoFechar: () => void;
  aoConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');

  return (
    <Dialog
      open={agente !== null}
      onClose={aoFechar}
      fullWidth
      maxWidth="xs"
      slotProps={{
        transition: {
          onEnter: () => setMotivo(''),
        },
      }}
    >
      <DialogTitle>Bloquear {agente?.nome}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText variant="body2">
            O token desta instalação para de ser aceito na próxima tentativa de envio. As
            outras instalações continuam funcionando, e nada do que já foi arquivado é
            apagado.
          </DialogContentText>

          <TextField
            label="Motivo"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            required
            autoFocus
            fullWidth
            multiline
            minRows={2}
            placeholder="Estava enviando nota do cliente errado"
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={aoFechar}>Cancelar</Button>
        <Button
          variant="contained"
          color="error"
          disabled={motivo.trim().length < 3}
          onClick={() => aoConfirmar(motivo.trim())}
        >
          Bloquear
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) return erro.message;
  return erro instanceof Error ? erro.message : 'Falha inesperada.';
}
